/**
 * Quote and job numbers (MB decision 119, Q103 A): Teslyn gives the next quote number per tenant, `Q` + 2-digit year
 * + 3-digit sequence (Thomson's practice, TJR inventory 01 §2.7.3: Q26048); the job number takes the same digits with
 * the job-type letter (F26048 fixed wing, H… helicopter), checked for duplicates. The user may type a number by hand.
 */
import type { ValidationIssue } from './model.js';
import type { JobType } from './quote-model.js';

const QUOTE_RE = /^Q(\d{2})(\d{3})$/;

/** The next free number for the year of `date` (YYYY-MM-DD), after the highest sequence already used that year. */
export function nextQuoteNumber(existing: readonly string[], date: string): string {
  const yy = date.slice(2, 4);
  let max = 0;
  for (const n of existing) {
    const m = QUOTE_RE.exec(n.trim().toUpperCase());
    if (m && m[1] === yy) max = Math.max(max, Number.parseInt(m[2]!, 10));
  }
  if (max >= 999) throw new RangeError(`All 999 quote numbers for 20${yy} are used.`);
  return `Q${yy}${String(max + 1).padStart(3, '0')}`;
}

export function jobLetter(jobType: JobType): 'F' | 'H' {
  return jobType === 'helicopter' ? 'H' : 'F';
}

/** "Q26048" + fixed wing → "F26048". */
export function jobNumberFromQuote(quoteNumber: string, jobType: JobType): string | null {
  const m = QUOTE_RE.exec(quoteNumber.trim().toUpperCase());
  return m ? `${jobLetter(jobType)}${m[1]}${m[2]}` : null;
}

/** A typed or proposed quote number: the Q + 5 digit form, not used by another quote of the tenant. */
export function validateQuoteNumber(number: string, others: readonly string[]): ValidationIssue[] {
  const n = number.trim().toUpperCase();
  if (!QUOTE_RE.test(n)) {
    return [
      { field: 'number', message: 'A quote number is Q, the 2-digit year and 3 digits, for example Q26048.' },
    ];
  }
  if (others.some((o) => o.trim().toUpperCase() === n)) {
    return [{ field: 'number', message: `${n} is already used by another quote.` }];
  }
  return [];
}

/** The project (job) number for a contract must not be used by another project of the tenant. */
export function validateJobNumber(number: string, projectNumbers: readonly string[]): ValidationIssue[] {
  const n = number.trim().toUpperCase();
  if (!/^[FH]\d{5}$/.test(n)) {
    return [{ field: 'number', message: 'A job number is F or H and 5 digits, for example F26048.' }];
  }
  if (projectNumbers.some((p) => p.trim().toUpperCase() === n)) {
    return [{ field: 'number', message: `Project ${n} already exists.` }];
  }
  return [];
}
